import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, Typography } from '@material-ui/core'
import { SitesClient, SiteResponseDTO, OrganizationsClient, OrganizationResponseDTO } from '../generated/ApiClient'
import MUIDataTable from "mui-datatables";
import { Link as RouterLink } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { actionCreators as snackbar } from '../store/AlertSnackbarStore'
import { tableOptions as options } from '../common/Shared'
import { useIsUserInRoles, OrgSiteModemEditor } from '../hooks/useIsUserInRoles';

interface SitesData {
    siteName: string;
    orgName: string;
    organizationId: string;
    externalId: string;
    address: string
    contact: string
}

const sortAndFilter = {
    sort: true,
    filter: true
};

export default function SitesPage() {
    return (
        <>
            <Typography variant='h4' id="tabelLabel">Sites</Typography>
            <Sites organizationId={undefined} />
        </>
    )
}

export function Sites(props: { organizationId: string | undefined }) {
    const [sitesData, setSitesData] = useState<SitesData[]>([]);
    const canEditSites = useIsUserInRoles(OrgSiteModemEditor)

    const tableOptions = {
        ...options,
        selectableRowsHideCheckboxes: !canEditSites,
        onRowsDelete: ((rowsDeleted: { lookup: any; data: { index: number; dataIndex: number; }[]; }, data: SitesData[]) => {
            const selectedIndexs = rowsDeleted.data.map((row) => row.dataIndex);
            const sitesIds: string[] = selectedIndexs.map((index) => sitesData![index].externalId);
            removeSites(sitesIds);
        })
    }

    const dispatch = useDispatch()
    const removeSites = async (siteIds: string[]) => {
        try {
            for (const id of siteIds) {
                await new SitesClient().delete2(id);
                setSitesData(sitesData.filter(x => x.externalId !== id))
            }
            dispatch(snackbar.addMessage({ title: "Success", message: `Sites successfully removed`, alertType: 'success' }))
        }
        catch (error) {
            dispatch(snackbar.addError(error, undefined, 'Error writing to the database'));
        }
    }

    const columnsWithOrg = [
        {
            name: "siteName",
            label: "Site",
            options: sortAndFilter
        },
        {
            name: "address",
            label: "Address",
            options: sortAndFilter
        },
        {
            name: "contact",
            label: "Person of Contact",
            options: sortAndFilter
        },
        {
            name: "orgName",
            label: "Organization",
            options: sortAndFilter
        },
        {
            name: 'detailsButton',
            label: 'View',
            options: {
                sort: false,
                filter: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const link = `/sites/${sitesData[dataIndex].externalId}`;
                    return <Button component={RouterLink} variant='contained' size='small' to={link}>View</Button>
                }
            }
        },
    ];
    const columnsWithOutOrg = [
        {
            name: "siteName",
            label: "Site",
            options: sortAndFilter
        },
        {
            name: "address",
            label: "Address",
            options: sortAndFilter
        },
        {
            name: "contact",
            label: "Person of Contact",
            options: sortAndFilter
        },
        {
            name: 'detailsButton',
            label: 'View',
            options: {
                sort: false,
                filter: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const link = `/sites/${sitesData[dataIndex].externalId}`;
                    return <Button component={RouterLink} variant='contained' size='small' to={link}>View</Button>
                }
            }
        },
    ];

    function toViewData(siteData: SiteResponseDTO, orgData: OrganizationResponseDTO[]) {
        let orgName = "";
        const contact = (siteData.firstName ?? "") + " " + (siteData.lastName ?? "") + "  " + (siteData.phoneNumber ?? "");
        const address = (siteData.address ?? "") + (siteData.city ? `, ${siteData.city}` : "") + (siteData.stateProv ? `, ${siteData.stateProv}` : "");
        const orgForSite = orgData.find(org => org.externalId === siteData.organizationId);
        if (orgForSite) {
            orgName = orgForSite.name;
        }
        return { siteName: siteData.name, address, externalId: siteData.externalId, contact, orgName, organizationId: "" } // TODO: fix org id
    }

    useEffect(() => {
        const fetchData = async () => {
            let sitesData: SiteResponseDTO[] = [];
            if (props.organizationId) {
                sitesData = await new OrganizationsClient().getAllSites(props.organizationId);
            }
            else {
                sitesData = await new SitesClient().getMany2();
            }
            if (sitesData.length > 0) {
                const organizationsData = await new OrganizationsClient().getMany();
                setSitesData(sitesData.map(s => toViewData(s, organizationsData)));
            }
        }
        fetchData();
    }, [props.organizationId])

    return (
        <>
            {canEditSites &&
                <Grid container item direction='column' spacing={2} alignItems='flex-end'>
                    <Box my={2}>
                        <Button component={RouterLink} variant='contained' color='primary' size='medium' to={props.organizationId ? `/organizations/${props.organizationId}/addSite` : `/sites/create`}>+ Create</Button>
                    </Box>
                </Grid>}
            <MUIDataTable
                title={""}
                data={sitesData}
                columns={props.organizationId ? columnsWithOutOrg : columnsWithOrg}
                options={tableOptions}
            />
        </>
    )
}